import type { IRequestGetOne } from '../../../../interfaces/requestGetOne';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import type { IOsasc1CustomControlData } from './Osasc1V1DiagnosticReportAddEditCustomControlFields';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { GradingSummary } from '../../../../../../common/enums/gradingSummary';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { SendSleepRecorderOption } from '../../../../../../common/validation/customControls/somnology';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import {
    Osasc1V1DiagnosticReportAddEditCustomControlFields,
    osasc1V1DiagnosticReportInitializeCustomControlData,
} from './Osasc1V1DiagnosticReportAddEditCustomControlFields';

interface IInnerOsasc1V1DiagnosticReportAddEditState {
    buttonDisabled?: boolean;
    showHandoverSleepRecorderMessage?: boolean;
}

export function Osasc1V1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    return <InnerOsasc1V1DiagnosticReportAddEdit {...props} />;
}

export class InnerOsasc1V1DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps,
    IInnerOsasc1V1DiagnosticReportAddEditState
> {
    public async componentDidMount() {
        if (
            !this.props.currentDiagnosticReport &&
            this.props.request.customControlData &&
            this.props.request.customControlData.referralRequestId
        ) {
            const result = await fetchJson(
                `/api/requests/${this.props.request.customControlData.referralRequestId}/customControlData`,
                'GET'
            );
            const referral: IRequestGetOne = result.json;
            const dataFromReferral: Partial<IOsasc1CustomControlData> = {};

            if (referral.customControlData && referral.customControlData.relevante_klachten) {
                if (referral.customControlData.relevante_klachten.vermoeidheid_overdag) {
                    dataFromReferral.vermoeidheid_overdag_value = 'Ja';
                }
                if (referral.customControlData.relevante_klachten.stokkende_ademhaling_tijdens_de_slaap) {
                    dataFromReferral.stokkende_ademhaling_value = 'Ja';
                }
                if (referral.customControlData.relevante_klachten.niet_verfrissende_slaap) {
                    dataFromReferral.niet_verfrissende_slaap_value = 'Ja';
                }
                if (referral.customControlData.relevante_klachten.veel_snurken) {
                    dataFromReferral.snurken_value = 'Ja';
                }
                if (referral.customControlData.relevante_klachten.verminderde_concentratie) {
                    dataFromReferral.concentratieverlies_value = 'Ja';
                }
                if (referral.customControlData.relevante_klachten.meer_dan_5_ademstops_per_uur) {
                    dataFromReferral.ademstops_value = 'Ja';
                }
            }

            if (referral.customControlData && referral.customControlData.relevante_klachten_ksyos) {
                const relevanteKlachten = referral.customControlData.relevante_klachten_ksyos;
                if (relevanteKlachten.ademstops_tijdens_het_slapen) {
                    dataFromReferral.ademstops_value =
                        relevanteKlachten.ademstops_tijdens_het_slapen === 'Nooit' ? 'Nee' : 'Ja';
                }
                if (relevanteKlachten.snurken) {
                    dataFromReferral.snurken_value = relevanteKlachten.snurken === 'Nooit' ? 'Nee' : 'Ja';
                }
                if (
                    relevanteKlachten.wakker_schrikken_met_het_gevoel_te_stikken &&
                    this.props.request.customControlData.somnologyOrganizationType !==
                        SomnologyOrganizationType.ksyosMedicalCenter
                ) {
                    dataFromReferral.herhaald_wakker_schrikken_value =
                        relevanteKlachten.wakker_schrikken_met_het_gevoel_te_stikken === 'Nooit' ? 'Nee' : 'Ja';
                }
            }

            if (
                !referral.customControlData?.slaaprecorderVerzenden ||
                [SendSleepRecorderOption.giveToThePatient, SendSleepRecorderOption.giveToThePatientKsyos].includes(
                    referral.customControlData.slaaprecorderVerzenden
                )
            ) {
                await this.setState({ showHandoverSleepRecorderMessage: true });
            }

            if (Object.keys(dataFromReferral).length > 0) {
                await this.handleChangeCustomControl(dataFromReferral);
            }

            await this.handleChangeCustomControl({
                somnologyOrganizationType: this.props.request.customControlData.somnologyOrganizationType,
            });
        } else if (this.props.currentDiagnosticReport) {
            const initializedCustomControlData = osasc1V1DiagnosticReportInitializeCustomControlData();
            await this.props.handleChangeCustomControl({
                ...initializedCustomControlData,
                ...this.props.currentDiagnosticReport.customControlData,
                comorbiditeiten: {
                    ...initializedCustomControlData.comorbiditeiten,
                    ...this.props.currentDiagnosticReport.customControlData.comorbiditeiten,
                },
            });
        }
    }

    public render() {
        return (
            <ProcessContainer>
                <ProcessItemHeader
                    isFirstItem
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    serviceType={ServiceType.consultation}
                    title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
                />

                <Form onSubmit={() => null}>
                    <Osasc1V1DiagnosticReportAddEditCustomControlFields
                        {...this.props}
                        customControlData={this.props.diagnosticReport.customControlData}
                        request={this.props.request}
                    />

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);

        const result = await fetchJson(`/api/diagnosticReports/${diagnosticReportId}`, 'GET');
        if (saveType === 'final') {
            if (result.json.gradingSummary === GradingSummary.deviant) {
                const toastTranslateKey = this.state.showHandoverSleepRecorderMessage
                    ? 'osas.handoverSleepRecorder'
                    : 'osas.firstConsultSaved';
                showServiceToast(
                    ToastType.success,
                    toastTranslateKey,
                    this.props.service,
                    'request',
                    translateKey(
                        'toast.answerSentToOrganizationSuccess',
                        this.props.request.requesterOrganization?.name
                    )
                );
            } else {
                showServiceToast(
                    ToastType.success,
                    'toast.submitSuccess',
                    this.props.service,
                    'request',
                    translateKey(
                        'toast.answerSentToOrganizationSuccess',
                        this.props.request.requesterOrganization?.name
                    )
                );
            }
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);
    }

    private async handleChangeCustomControl(value: Partial<IOsasc1CustomControlData>) {
        await this.props.handleChangeCustomControl(value);
    }
}
